import { useQuery } from '@tanstack/react-query';

interface HealthResponse {
  mongo: 'ok' | 'error';
  latencyMs: number;
  collections: Record<string, number>;
}

async function fetchHealth(): Promise<HealthResponse> {
  const r = await fetch('/api/health');
  if (!r.ok) throw new Error(`health ${r.status}`);
  return r.json();
}

export function BootCheck() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health'],
    queryFn: fetchHealth,
  });

  return (
    <main className="min-h-full bg-surface-bg text-fg p-8">
      <header className="flex items-center gap-3 mb-8">
        <img src="/malsi-logo.svg" alt="Malsi" className="h-8 w-auto" />
        <div className="h-6 w-px bg-surface-divider" />
        <span className="text-fg-muted text-sm font-medium uppercase tracking-wide">
          Admin · boot check
        </span>
      </header>

      <section className="max-w-2xl">
        <h1 className="text-2xl font-bold mb-2">Phase 1 · slice A+B</h1>
        <p className="text-fg-muted mb-6">
          If you can see this with the Tommy font rendering and the API health
          card below showing <span className="text-brand-cyan">mongo: ok</span>,
          the dev stack is wired up.
        </p>

        <div className="rounded-xl border border-surface-divider bg-surface-raised p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-fg-muted mb-3">
            /api/health
          </div>
          {isLoading && <div className="text-fg-muted">checking…</div>}
          {error && (
            <div className="text-state-danger">
              error: {(error as Error).message}
            </div>
          )}
          {data && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-fg-muted text-sm">mongo</span>
                <span
                  className={
                    data.mongo === 'ok'
                      ? 'text-brand-cyan font-bold'
                      : 'text-state-danger font-bold'
                  }
                >
                  {data.mongo}
                </span>
                <span className="text-fg-subtle text-sm">
                  ({data.latencyMs} ms)
                </span>
              </div>
              <div className="text-xs font-bold uppercase tracking-wide text-fg-muted pt-2">
                collections
              </div>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {Object.entries(data.collections)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([name, n]) => (
                    <li key={name} className="flex justify-between">
                      <span className="text-fg-muted">{name}</span>
                      <span className="text-fg font-medium tabular-nums">
                        {n.toLocaleString()}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
